import { useCallback, useEffect, useState } from "react";
import { Lock, CalendarOff, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "../ui";

const FIELD = "bg-white border border-sand-200 rounded-lg px-2.5 py-1.5 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const INCREMENTS = [15, 30, 45, 60];

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};
const fmt12 = (t) => {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

export default function DaySlotEditor({ employees, types, onSaved }) {
  const [employeeId, setEmployeeId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [date, setDate] = useState(tomorrow());
  const [increment, setIncrement] = useState(60);
  const [grid, setGrid] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (employees.length && !employees.some((e) => e.id === employeeId)) setEmployeeId(employees[0].id);
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = types.find((x) => x.id === typeId);
    if (t?.slot_increment_minutes) setIncrement(t.slot_increment_minutes);
  }, [typeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!employeeId || !date) return;
    api.get("/admin/schedule-day", { params: { employee_id: employeeId, date, increment } })
      .then((r) => { setGrid(r.data); setDirty(false); })
      .catch(() => setGrid(null));
  }, [employeeId, date, increment]);
  useEffect(load, [load]);

  const toggle = (i) => {
    setGrid((g) => {
      if (!g || g.slots[i].status === "booked") return g;
      const slots = g.slots.map((s, j) => (j === i ? { ...s, status: s.status === "open" ? "blocked" : "open" } : s));
      return { ...g, slots };
    });
    setDirty(true);
  };

  const save = async (mode) => {
    if (!grid) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/schedule-day", {
        employee_id: employeeId, date, increment, mode,
        open_times: grid.slots.filter((s) => s.status === "open").map((s) => s.time),
      });
      setGrid(data);
      setDirty(false);
      toast.success(mode === "block_day" ? "Day blocked" : mode === "clear" ? "Reset to weekly hours" : "Day saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const emp = employees.find((e) => e.id === employeeId);

  return (
    <Card className="mb-6" data-testid="day-slot-editor">
      <h3 className="font-medium text-charcoal-800 mb-1">Day slot editor</h3>
      <p className="mb-4 text-xs text-charcoal-500">Pick an employee and a day, then click slots to open or block them — e.g. block 12&nbsp;PM for lunch. Saving stores it as a date-specific override for that employee.</p>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-sm text-charcoal-600 min-w-0">Employee
          <select data-testid="grid-employee" className={`${FIELD} mt-1 w-full`} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select></label>
        <label className="text-sm text-charcoal-600 min-w-0">Appointment type (sets increments)
          <select data-testid="grid-type" className={`${FIELD} mt-1 w-full`} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">— just pick an increment —</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></label>
        <label className="text-sm text-charcoal-600 min-w-0">Day
          <input data-testid="grid-date" type="date" className={`${FIELD} mt-1 w-full`} value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="text-sm text-charcoal-600 min-w-0">Slot increments
          <select data-testid="grid-increment" className={`${FIELD} mt-1 w-full`} value={increment} onChange={(e) => setIncrement(Number(e.target.value))}>
            {INCREMENTS.map((n) => <option key={n} value={n}>{n} min</option>)}
          </select></label>
      </div>

      {!grid ? (
        <p className="text-sm text-charcoal-500">Select an employee and day to load slots.</p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-charcoal-500">
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-sage" /> open</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-sand-200" /> blocked</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-clay-100 border border-clay-800/20" /> booked (locked)</span>
            {grid.blocked_day && <span className="rounded-full bg-clay-100 px-2 py-0.5 text-clay-800">entire day blocked</span>}
            {grid.has_override && !grid.blocked_day && <span className="rounded-full bg-sage-light px-2 py-0.5 text-sage-700">custom hours saved for this date</span>}
          </div>

          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8">
            {grid.slots.map((s, i) => (
              <button key={s.time} type="button" data-testid={`grid-slot-${s.time}`} onClick={() => toggle(i)}
                disabled={s.status === "booked"} title={s.status === "booked" ? s.booking : undefined}
                className={`flex items-center justify-center gap-1 truncate rounded-xl border px-1 py-2.5 text-xs font-medium transition-colors
                  ${s.status === "open" ? "bg-sage text-white border-sage hover:bg-sage-hover"
                    : s.status === "booked" ? "bg-clay-100 text-clay-800 border-clay-800/20 cursor-not-allowed"
                      : "bg-sand-100 text-charcoal-400 border-sand-200 hover:border-terracotta"}`}>
                {s.status === "booked" && <Lock size={11} className="shrink-0" />}{fmt12(s.time)}
              </button>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button data-testid="grid-save" onClick={() => save("override")} disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving…" : `Save ${emp?.name || ""}'s day`}
            </button>
            <button data-testid="grid-block-day" onClick={() => save("block_day")} disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-sand-200 px-5 py-2 text-sm text-charcoal-600 hover:border-terracotta hover:text-terracotta">
              <CalendarOff size={14} /> Block entire day
            </button>
            <button data-testid="grid-clear" onClick={() => save("clear")} disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-sand-200 px-5 py-2 text-sm text-charcoal-600 hover:border-sage">
              <RotateCcw size={14} /> Reset to weekly hours
            </button>
            {dirty && <span className="text-xs text-terracotta">Unsaved changes</span>}
          </div>
        </>
      )}
    </Card>
  );
}
